import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { obtenerPrograma } from '../../servicios/programaService';
import { obtenerConfigCalidad, actualizarAvanceCalidad } from '../../servicios/calidadService';
import '../estilos/Calidad.css';

const ESTADO_ITO_LABEL = {
  pendiente: 'R1',
  rf: 'En RF',
  entregado: '✓ Entregado',
  observado: '⚠ Observaciones R1'
};

const ESTADO_ITO_CLASE = {
  pendiente: 'pendiente',
  rf: 'rf',
  entregado: 'completado',
  observado: 'atrasado'
};

// Orden de la tabla: Observaciones R1 → Observaciones RF → En RF → R1 → Entregado (al final)
function prioridadOrden(act) {
  const estado = act.estadoITO || 'pendiente';
  const rondas = act.rondasRF || [];
  if (estado === 'observado') return 0;
  if (estado === 'rf' && rondas.length > 0) return 1;
  if (estado === 'rf') return 2;
  if (estado === 'pendiente') return 3;
  return 4; // entregado
}

// Primera ronda RF se etiqueta "RF" sin número; desde la segunda se numera RF1, RF2...
function labelRonda(i) {
  return i === 0 ? 'RF' : `RF${i}`;
}

function estadoChip(estadoITO, rondasRF) {
  if (estadoITO === 'rf' && rondasRF.length > 0) {
    return { label: '⚠ Observaciones RF', clase: 'atrasado' };
  }
  return { label: ESTADO_ITO_LABEL[estadoITO], clase: ESTADO_ITO_CLASE[estadoITO] };
}

function FilaSeguimiento({ act, maxRondas, onActualizarActividad }) {
  const estadoITO = act.estadoITO || 'pendiente';
  const rondasRF = act.rondasRF || [];
  const enRF = estadoITO === 'rf';
  const entregado = estadoITO === 'entregado';
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [escribiendoObs, setEscribiendoObs] = useState(false);
  const [textoObs, setTextoObs] = useState('');
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [editando, setEditando] = useState(false);
  const [edicionR1, setEdicionR1] = useState(null);
  const [edicionRondas, setEdicionRondas] = useState([]);
  const chip = estadoChip(estadoITO, rondasRF);

  const hayAlgoQueEditar = Boolean(act.observacionITO) || rondasRF.length > 0;

  const marcarEntregado = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarActividad(act._id, { estadoITO: 'entregado' });
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const marcarAprobado = async () => {
    setGuardando(true);
    setError(null);
    try {
      await onActualizarActividad(act._id, { estadoITO: 'entregado' });
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const abrirObservacion = () => {
    setTextoObs('');
    setError(null);
    setEscribiendoObs(true);
  };

  const guardarObservacion = async () => {
    setGuardando(true);
    setError(null);
    try {
      const nuevasRondas = [...rondasRF, { observacion: textoObs, fecha: new Date().toISOString() }];
      await onActualizarActividad(act._id, { rondasRF: nuevasRondas, estadoITO: 'rf' });
      setEscribiendoObs(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const abrirEdicion = () => {
    setMenuAbierto(false);
    setError(null);
    setEdicionR1(act.observacionITO || null);
    setEdicionRondas(rondasRF.map(r => r.observacion));
    setEditando(true);
  };

  const eliminarEdicionR1 = () => setEdicionR1(null);

  const eliminarEdicionRonda = (i) => {
    setEdicionRondas(prev => prev.map((v, idx) => idx === i ? null : v));
  };

  const guardarEdicion = async () => {
    setGuardando(true);
    setError(null);
    try {
      const payload = {};
      let seEliminoAlgo = false;

      if (act.observacionITO) {
        if (edicionR1 === null) {
          payload.observacionITO = '';
          seEliminoAlgo = true;
          // Solo retrocede a R1 si esa observación es lo que mantiene la partida
          // bloqueada ahí; el caso de 'entregado' se maneja abajo.
          if (estadoITO === 'observado') {
            payload.estadoITO = 'pendiente';
          }
        } else {
          payload.observacionITO = edicionR1;
        }
      }

      if (rondasRF.length) {
        const rondasFinal = rondasRF
          .map((r, i) => edicionRondas[i] === null ? null : { ...r, observacion: edicionRondas[i] });
        if (rondasFinal.some(r => r === null)) seEliminoAlgo = true;
        payload.rondasRF = rondasFinal.filter(r => r !== null);
      }

      // Editar/eliminar una observación de una partida ya "Entregado" la reabre a RF.
      if (seEliminoAlgo && estadoITO === 'entregado') {
        payload.estadoITO = 'rf';
      }

      await onActualizarActividad(act._id, payload);
      setEditando(false);
    } catch {
      setError('Error al guardar. Intenta de nuevo.');
    } finally {
      setGuardando(false);
    }
  };

  const rondasVisiblesEnEdicion = edicionRondas
    .map((texto, i) => ({ texto, i }))
    .filter(r => r.texto !== null);

  return (
    <tr>
      <td style={{ fontWeight: 500, maxWidth: 280 }}>
        {act._rutaGrupos?.length > 0 && (
          <div className="calidad-obs-ruta-grupos">
            {act._rutaGrupos.map((n, i) => (
              <span key={i}>
                {i > 0 && <span className="calidad-obs-ruta-sep">›</span>}
                <span className="calidad-obs-ruta-item">{n}</span>
              </span>
            ))}
          </div>
        )}
        {act.nombre}
      </td>
      <td>
        <span className={`calidad-estado calidad-estado-${chip.clase}`}>
          {chip.label}
        </span>
      </td>
      {Array.from({ length: maxRondas }).map((_, i) => (
        <td key={i} style={{ whiteSpace: 'pre-wrap', color: '#374151', fontSize: 13, maxWidth: 220 }}>
          {rondasRF[i]?.observacion || ''}
        </td>
      ))}
      <td style={{ minWidth: 220, position: 'relative' }}>
        {editando ? (
          <div className="calidad-edit-panel">
            {act.observacionITO && (
              <div className="calidad-edit-row">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>R1</div>
                  {edicionR1 === null ? (
                    <span style={{ fontSize: 12, color: '#dc2626' }}>Se eliminará al guardar</span>
                  ) : (
                    <textarea value={edicionR1} onChange={e => setEdicionR1(e.target.value)} disabled={guardando} />
                  )}
                </div>
                {edicionR1 !== null && (
                  <button className="calidad-btn-secondary" onClick={eliminarEdicionR1} disabled={guardando} title="Eliminar">
                    🗑
                  </button>
                )}
              </div>
            )}
            {rondasVisiblesEnEdicion.map(({ texto, i }, idx) => (
              <div className="calidad-edit-row" key={i}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 2 }}>{labelRonda(idx)}</div>
                  <textarea value={texto} onChange={e => {
                    const val = e.target.value;
                    setEdicionRondas(prev => prev.map((v, idx2) => idx2 === i ? val : v));
                  }} disabled={guardando} />
                </div>
                <button className="calidad-btn-secondary" onClick={() => eliminarEdicionRonda(i)} disabled={guardando} title="Eliminar">
                  🗑
                </button>
              </div>
            ))}
            <div className="calidad-edit-panel-actions">
              <button className="calidad-btn-secondary" onClick={() => setEditando(false)} disabled={guardando}>
                Cancelar
              </button>
              <button className="calidad-btn-primary" onClick={guardarEdicion} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        ) : escribiendoObs ? (
          <div className="calidad-edit-panel">
            <textarea
              placeholder="Describe la observación detectada por ITO…"
              value={textoObs}
              onChange={e => setTextoObs(e.target.value)}
              disabled={guardando}
            />
            <div className="calidad-edit-panel-actions">
              <button className="calidad-btn-secondary" onClick={() => setEscribiendoObs(false)} disabled={guardando}>
                Cancelar
              </button>
              <button className="calidad-btn-primary" onClick={guardarObservacion} disabled={guardando || !textoObs.trim()}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {!entregado && !enRF && (
              <>
                <button className="calidad-btn-editar calidad-btn-editar-verde" onClick={marcarEntregado} disabled={guardando}>
                  {guardando ? 'Guardando…' : 'Aprobado'}
                </button>
                <button className="calidad-btn-editar" onClick={abrirObservacion} disabled={guardando}>
                  Con observaciones
                </button>
              </>
            )}
            {enRF && (
              <>
                <button className="calidad-btn-editar calidad-btn-editar-verde" onClick={marcarAprobado} disabled={guardando}>
                  {guardando ? 'Guardando…' : 'Entregado'}
                </button>
                <button className="calidad-btn-editar" onClick={abrirObservacion} disabled={guardando}>
                  Observaciones
                </button>
              </>
            )}
            {hayAlgoQueEditar && (
              <div style={{ position: 'relative' }}>
                <button
                  className="calidad-btn-menu"
                  onClick={() => setMenuAbierto(v => !v)}
                  disabled={guardando}
                  title="Más opciones"
                >
                  ⋮
                </button>
                {menuAbierto && (
                  <>
                    <div
                      style={{ position: 'fixed', inset: 0, zIndex: 9 }}
                      onClick={() => setMenuAbierto(false)}
                    />
                    <div className="calidad-dropdown">
                      <button className="calidad-dropdown-item" onClick={abrirEdicion}>
                        Editar
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}
        {error && <span style={{ color: '#dc2626', fontSize: 11, display: 'block', marginTop: 4 }}>{error}</span>}
      </td>
    </tr>
  );
}

const FILTROS_ESTADO_ITO = [
  { valor: 'todos', label: 'Todos' },
  { valor: 'r1', label: 'R1' },
  { valor: 'rf', label: 'RF' },
  { valor: 'entregado', label: 'Entregados' }
];

export default function SeguimientoCalidad() {
  const [datos, setDatos] = useState({ programa: null, secciones: [], actividades: [] });
  const [actividadesCalidad, setActividadesCalidad] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const [prog, config] = await Promise.all([obtenerPrograma(), obtenerConfigCalidad()]);
      setDatos(prog);
      setActividadesCalidad(config);
    } catch {
      // silencioso
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const actualizarActividad = useCallback(async (actividadId, cambios) => {
    const actualizada = await actualizarAvanceCalidad(datos.programa._id, actividadId, cambios);
    setDatos(prev => ({
      ...prev,
      actividades: prev.actividades.map(a => a._id === actividadId ? { ...a, ...actualizada } : a)
    }));
  }, [datos.programa]);

  const actividadesSet = useMemo(() => new Set(actividadesCalidad), [actividadesCalidad]);

  const completadas = useMemo(() => {
    const mapaSeccion = {};
    for (const s of datos.secciones) {
      if (s.wbsCode) mapaSeccion[s.wbsCode] = s;
    }
    const mapaAct = {};
    for (const act of datos.actividades) {
      if (act.wbsCode) mapaAct[act.wbsCode] = act.nombre;
    }

    const getRuta = (wbsCode) => {
      const partes = wbsCode.split('.');
      const ruta = [];
      for (let i = 1; i < partes.length; i++) {
        const code = partes.slice(0, i).join('.');
        const nombre = i === 1
          ? mapaSeccion[code]?.nombre
          : mapaAct[code];
        if (nombre) ruta.push(nombre);
      }
      return ruta;
    };

    return datos.actividades
      .filter(act => act.wbsCode && actividadesSet.has(act.wbsCode) && (act.porcentajeAvance ?? 0) === 100)
      .map(act => ({ ...act, _rutaGrupos: getRuta(act.wbsCode).slice(-3) }))
      .sort((a, b) => {
        const pa = prioridadOrden(a), pb = prioridadOrden(b);
        if (pa !== pb) return pa - pb;
        return (a.orden ?? 0) - (b.orden ?? 0);
      });
  }, [datos.actividades, datos.secciones, actividadesSet]);

  const partidasFiltradas = useMemo(() => {
    if (filtroEstado === 'todos') return completadas;
    return completadas.filter(act => {
      const estado = act.estadoITO || 'pendiente';
      if (filtroEstado === 'r1') return estado === 'pendiente' || estado === 'observado';
      return estado === filtroEstado;
    });
  }, [completadas, filtroEstado]);

  const maxRondas = useMemo(
    () => Math.max(0, ...partidasFiltradas.map(a => (a.rondasRF || []).length)),
    [partidasFiltradas]
  );

  if (cargando) {
    return <div className="calidad-loading">Cargando seguimiento…</div>;
  }

  if (!datos.programa) {
    return (
      <div className="calidad-container">
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>No hay programa de obra importado</h3>
          <p>Importa un programa en el módulo Programa de Obra para usar esta vista.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calidad-container">
      <div className="calidad-header">
        <h2>ITO R1 - RF</h2>
      </div>

      {completadas.length === 0 ? (
        <div className="calidad-empty">
          <div className="calidad-empty-icon">📋</div>
          <h3>Sin partidas completadas</h3>
          <p>Las partidas completadas desde Actividades u Observaciones aparecerán aquí.</p>
        </div>
      ) : (
        <>
          <div className="calidad-filtros">
            {FILTROS_ESTADO_ITO.map(f => (
              <button
                key={f.valor}
                className={`calidad-filtro-chip ${filtroEstado === f.valor ? 'activo' : ''}`}
                onClick={() => setFiltroEstado(f.valor)}
              >
                {f.label}
              </button>
            ))}
          </div>

          {partidasFiltradas.length === 0 ? (
            <div className="calidad-empty">
              <div className="calidad-empty-icon">🔍</div>
              <h3>Sin partidas en este estado</h3>
              <p>Prueba con otro filtro de Estado ITO.</p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="calidad-tabla">
                <thead>
                  <tr>
                    <th>Partida</th>
                    <th>Estado ITO</th>
                    {Array.from({ length: maxRondas }).map((_, i) => (
                      <th key={i}>{`Obs ${labelRonda(i)}`}</th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {partidasFiltradas.map(act => (
                    <FilaSeguimiento key={act._id} act={act} maxRondas={maxRondas} onActualizarActividad={actualizarActividad} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
